import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api-client';

export interface StoryWorld {
  id: string;
  slug: string;
  productLine: string;
  sortOrder: number;
  usesStuffedAnimal: boolean;
  usesFriends: boolean;
  usesFamilyMembers: boolean;
  usesCustomTheme: boolean;
  requiresFamilyProfile: boolean;
  showRating: boolean;
  showShare: boolean;
  showLearning: boolean;
  language: string;
  name: string;
  description: string;
  tagline: string;
  characterNames: Record<string, unknown>;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

// Module-level cache: worlds rarely change, and every Builder + WorldSelectionSheet
// would otherwise duplicate the same public GET on mount. Shared promise dedupes
// concurrent first-mount fetches from different components.
let cache: StoryWorld[] | null = null;
let inflight: Promise<StoryWorld[]> | null = null;

function fetchWorlds(): Promise<StoryWorld[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = apiFetch<StoryWorld[]>('/api/story-worlds')
    .then((worlds) => {
      cache = worlds;
      return worlds;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

interface UseStoryWorldsResult {
  worlds: StoryWorld[] | null;
  getWorldIdBySlug: (slug: string) => string | null;
  getWorldSlugById: (id: string) => string | null;
  isLoading: boolean;
  error: string | null;
}

export function useStoryWorlds(): UseStoryWorldsResult {
  const [worlds, setWorlds] = useState<StoryWorld[] | null>(cache);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Initial state already reflects module-level cache (via useState initializer)
    // — skip the fetch when cache is already populated.
    if (cache) return;
    let cancelled = false;
    fetchWorlds()
      .then((list) => {
        if (!cancelled) setWorlds(list);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Welten konnten nicht geladen werden.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const getWorldIdBySlug = (slug: string): string | null => {
    const match = worlds?.find((w) => w.slug === slug);
    return match?.id ?? null;
  };

  const getWorldSlugById = (id: string): string | null => {
    const match = worlds?.find((w) => w.id === id);
    return match?.slug ?? null;
  };

  return {
    worlds,
    getWorldIdBySlug,
    getWorldSlugById,
    isLoading: worlds === null && error === null,
    error,
  };
}
